import type { ScenarioRunStep } from './api';

export interface ExportPayload {
  generatedAt: string;
  scenarioId: string;
  rulesVersion: string | null;
  steps: { label: string; run: unknown }[];
}

/**
 * Field-name patterns redacted from any exported evidence, per
 * 08-seguridad-privacidad.md ("la exportación redacta campos configurados").
 * The current run/output contract never carries secrets, but this keeps the
 * control real and in place for any future field that would.
 */
const REDACTED_FIELD_PATTERN = /(secret|signature|token|password|authorization|cookie)/i;
const REDACTED_VALUE = '[redacted]';

function redact(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(redact);
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, val]) => [
        key,
        REDACTED_FIELD_PATTERN.test(key) ? REDACTED_VALUE : redact(val),
      ]),
    );
  }
  return value;
}

export function buildExportPayload(scenarioId: string, steps: ScenarioRunStep[]): ExportPayload {
  return {
    generatedAt: new Date().toISOString(),
    scenarioId,
    rulesVersion: steps[0]?.output.evidence.rulesVersion ?? null,
    steps: steps.map((step) => ({ label: step.label, run: redact(step.output) })),
  };
}

export function exportFileName(scenarioId: string): string {
  const stamp = new Date().toISOString().replace(/[:.]/g, '-');
  return `webhook-reliability-${scenarioId}-${stamp}.json`;
}

export function serializeExport(payload: ExportPayload): string {
  return JSON.stringify(payload, null, 2);
}
